import { useState } from "react";
import { X, Globe, Loader2, Check } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { submitCommunityDeal } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Native "Submit Alternative Website" — replaces the old Tally form. Signed-in users
// suggest a better/alternative deal source; it lands in the admin review queue.
export default function SubmitAlternativeWebsite({ open, onClose }) {
  const [itemName, setItemName] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  useBodyScrollLock(open);

  if (!open) return null;

  const reset = () => { setItemName(""); setUrl(""); setNote(""); setDone(false); setErr(""); };
  const close = () => { reset(); onClose?.(); };

  const submit = async () => {
    setErr("");
    if (!itemName.trim()) { setErr("Tell us what the deal is for."); return; }
    if (!/^https?:\/\/.+\..+/i.test(url.trim())) { setErr("Enter a valid website URL (https://…)."); return; }
    setBusy(true);
    try {
      await submitCommunityDeal({ item_name: itemName.trim(), website_url: url.trim(), note: note.trim() || null });
      setDone(true);
      toast.success("Thanks! Your suggestion was sent for review.");
    } catch (e) {
      setErr(e.message || "Submission failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" data-testid="submit-website-modal" onClick={close}>
      <div
        className="w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-yellow to-brand-orange">
              <Globe className="h-5 w-5 text-black" />
            </span>
            <div>
              <h3 className="text-base font-bold text-gray-900">Submit an Alternative Website</h3>
              <p className="text-xs text-gray-500">Know a cheaper source? Send it our way.</p>
            </div>
          </div>
          <button onClick={close} data-testid="submit-website-close" aria-label="Close" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="mt-6 flex flex-col items-center py-6 text-center" data-testid="submit-website-success">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <Check className="h-7 w-7 text-emerald-600" strokeWidth={3} />
            </span>
            <p className="mt-4 text-sm font-bold text-gray-900">Sent for review!</p>
            <p className="mt-1 text-xs text-gray-500">Our team will check it out. Thanks for helping keep it cheap.</p>
            <div className="mt-5 flex gap-3">
              <button onClick={reset} data-testid="submit-website-again" className="rounded-xl border border-gray-200 px-4 h-10 text-sm font-bold text-gray-700 hover:bg-gray-50">Submit another</button>
              <button onClick={close} className="rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange px-5 h-10 text-sm font-bold text-black">Done</button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.08em] text-gray-500">What's it for?</label>
              <input
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="e.g. AirPods Pro, hotel in Miami"
                data-testid="submit-website-item"
                className="mt-1.5 h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:ring-2 focus:ring-brand-yellow/60"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.08em] text-gray-500">Website URL</label>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                data-testid="submit-website-url"
                className="mt-1.5 h-11 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:ring-2 focus:ring-brand-yellow/60"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.08em] text-gray-500">Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Why is this a better deal?"
                data-testid="submit-website-note"
                className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-yellow/60"
              />
            </div>
            {err && <p className="text-sm text-red-500" data-testid="submit-website-error">{err}</p>}
            <button
              onClick={submit}
              disabled={busy}
              data-testid="submit-website-btn"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-yellow to-brand-orange py-3 text-sm font-bold uppercase tracking-[0.06em] text-black transition-transform hover:scale-[1.01] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Submit for Review
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
